import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./SegmentedLCDPage.css";

// ── Data ──────────────────────────────────────────────────────────────────────

const WHY_CARDS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="2" x2="12" y2="12" />
        <path d="M5.5 7A8 8 0 1 0 18.5 7" />
      </svg>
    ),
    title: "Ultra-Low Power",
    desc: "Ideal for battery-based devices ensuring long operational life with minimal energy draw.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    title: "High Contrast & Visibility",
    desc: "Crisp readability even in bright outdoor lighting conditions without backlighting.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
    title: "Every Segment, Custom-Drawn",
    desc: "Numeric, alphanumeric and icon-based layouts — there is no standard part number, only your layout.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 2 7 12 12 22 7 12 2" />
        <polyline points="2 17 12 22 22 17" />
        <polyline points="2 12 12 17 22 12" />
      </svg>
    ),
    title: "Rugged & Reliable",
    desc: "Built using premium RoHS-certified materials for long-term stability and durability.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <line x1="8" y1="20" x2="16" y2="20" />
        <line x1="12" y1="16" x2="12" y2="20" />
      </svg>
    ),
    title: "Multiple Technologies",
    desc: "TN, HTN, STN, FSTN — reflective, transflective or transmissive, chosen to fit your application.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    title: "Long Lifecycle",
    desc: "Stable optical performance over extended operational periods for industrial-grade reliability.",
  },
];

const LCD_TYPES = [
  {
    type: "TN",
    name: "Twisted Nematic",
    image: "/assets/images/lcd-type-tn.jpg",
    how: "Standard twisted-nematic liquid crystal with single-segment addressing.",
    contrast: "Reflective, moderate contrast, wide viewing angle",
    bestFor: "Cost-sensitive counters & basic digital meters",
  },
  {
    type: "HTN",
    name: "High-Contrast TN",
    image: "/assets/images/lcd-type-htn.jpg",
    how: "A higher pre-tilt TN formulation tuned for sharper on/off contrast.",
    contrast: "Reflective / transflective, high contrast",
    bestFor: "Panel meters & handheld instruments needing crisp digits",
  },
  {
    type: "STN",
    name: "Super-Twisted Nematic",
    image: "/assets/images/lcd-type-stn.jpg",
    how: "A steeper molecular twist gives a sharp threshold response, allowing many more segments on one drive line.",
    contrast: "Reflective / transflective, higher contrast at high multiplex ratios",
    bestFor: "Multi-digit, icon-heavy panels — energy meters, data loggers",
  },
  {
    type: "FSTN",
    name: "Film-Compensated STN",
    image: "/assets/images/lcd-type-fstn.jpg",
    how: "A retardation film corrects STN's natural blue/yellow tint for a true black-on-white image.",
    contrast: "Transflective / transmissive, works well with backlight",
    bestFor: "Backlit instrument clusters, automotive & outdoor equipment",
  },
];

const APPLICATIONS = [
  {
    key: "remote",
    title: "Remote Controls",
    desc: "Compact segment counters for channel, volume and mode indication on a coin-cell budget.",
    image: "/assets/images/app-remote-control.jpg",
  },
  {
    key: "energy",
    title: "Energy Meters",
    desc: "Multi-digit, multi-line readouts for kWh, voltage and tariff data that stay legible for years in the field.",
    image: "/assets/images/app-energy-meter.jpg",
  },
  {
    key: "cluster",
    title: "Instrument Clusters",
    desc: "Color PMVA cluster combining a bar-graph tachometer, digital speedometer and warning icons in one custom panel.",
    image: "/assets/images/app-instrument-cluster-pmva.jpg",
  },
  {
    key: "logger",
    title: "Data Loggers",
    desc: "Rugged, low-power panels for industrial sensors and field instruments running for months on a battery.",
    image: "/assets/images/app-data-logger.jpg",
  },
  {
    key: "calculator",
    title: "Calculators",
    desc: "Wide, high-digit-count strips with decimal and sign icons, tuned for indoor reflective readability.",
    image: "/assets/images/app-calculator.jpg",
  },
  {
    key: "3phase",
    title: "3-Phase Energy Meters",
    desc: "Multi-line L1/L2/L3 voltage, current and kWh readouts for DIN-rail and panel-mount industrial meters.",
    image: "/assets/images/app-3phase-meter.jpg",
  },
  {
    key: "water",
    title: "Water Meters",
    desc: "Flow-rate and cumulative volume digits built to stay readable through years outdoors or underground.",
    image: "/assets/images/app-water-meter.jpg",
  },
  {
    key: "gas",
    title: "Gas Meters",
    desc: "Low-power cumulative volume and pressure readouts engineered for long-life, tamper-aware gas metering.",
    image: "/assets/images/app-gas-meter.jpg",
  },
  {
    key: "lock",
    title: "Smart Locks",
    desc: "Compact status and keypad-entry displays for battery-powered smart locks and access panels.",
    image: "/assets/images/app-smart-lock.jpg",
  },
  {
    key: "watch",
    title: "Digital Watches",
    desc: "Small, high-contrast time and date segments legible outdoors, running for years on a coin cell.",
    image: "/assets/images/app-digital-watch.jpg",
  },
  {
    key: "thermometer",
    title: "Digital Thermometers",
    desc: "Temperature and humidity readouts sized for compact wall, desk and handheld instrument housings.",
    image: "/assets/images/app-thermometer.jpg",
  },
  {
    key: "panel",
    title: "Panel Meters",
    desc: "Flush-mount instrument displays for voltage, current and process readouts on electrical enclosures.",
    image: "/assets/images/app-panel-meter.jpg",
  },
];

const APPLICATIONS_EXTRA = [
  "Medical & health monitoring devices",
  "Industrial control instruments",
  "Home appliances & consumer electronics",
  "Weighing scales & measurement tools",
];

const KEY_FEATURES = [
  "Operating voltage: 3V / 5V / custom",
  "Wide industrial temperature range",
  "High viewing angle capability",
  "RoHS-certified materials",
  "Custom PCB layouts & connector interfaces",
  "Long lifespan & stable optical performance",
  "Backlight options: None / LED / EL",
  "Digit count: 3½ to 6+ digits",
];

const DESIGN_PROCESS = [
  "Requirement capture — application, enclosure, digit & icon count",
  "Segment and icon layout drawn in-house to match your PCB",
  "Prototype sample built and shared for your validation",
  "Mass production to the approved custom spec",
];

// Each spec's values link to its own knowledge-base article at
// /learn/<slug> (content in src/data/learnArticles.js) -- clicking "TN" or
// "Reflective" takes a visitor to a full write-up of what it is, how it
// works, and its pros/cons versus the alternatives in that same row.
const SPECS = [
  {
    label: "Technology",
    // All four link to the same single comparison article rather than one
    // page each -- see src/data/learnArticles.js's Technology entry.
    items: [
      { text: "TN", slug: "tn-vs-htn-vs-stn-vs-fstn-lcd-comparison" },
      { text: "HTN", slug: "tn-vs-htn-vs-stn-vs-fstn-lcd-comparison" },
      { text: "STN", slug: "tn-vs-htn-vs-stn-vs-fstn-lcd-comparison" },
      { text: "FSTN", slug: "tn-vs-htn-vs-stn-vs-fstn-lcd-comparison" },
    ],
  },
  {
    label: "Mode",
    items: [
      { text: "Reflective", slug: "reflective-lcd-mode" },
      { text: "Transmissive", slug: "transmissive-lcd-mode" },
      { text: "Transflective", slug: "transflective-lcd-mode" },
    ],
  },
  {
    label: "Voltage",
    items: [
      { text: "3V", slug: "3v-lcd-drive-voltage" },
      { text: "5V", slug: "5v-lcd-drive-voltage" },
      { text: "Custom", slug: "custom-lcd-drive-voltage" },
    ],
  },
  {
    label: "Temperature",
    items: [
      { text: "-20°C to +70°C (Industrial)", slug: "industrial-temperature-range-lcd" },
    ],
  },
  {
    label: "Backlight",
    items: [
      { text: "None", slug: "no-backlight-reflective-lcd" },
      { text: "LED", slug: "led-backlight-lcd" },
      { text: "EL", slug: "el-backlight-lcd" },
    ],
  },
  {
    label: "Interface",
    items: [
      { text: "Static", slug: "static-drive-lcd" },
      { text: "Multiplex (1/2, 1/3, 1/4)", slug: "multiplex-drive-lcd" },
    ],
  },
  {
    label: "Compliance",
    items: [
      { text: "RoHS Certified", slug: "rohs-compliance-lcd" },
    ],
  },
  {
    label: "Customization",
    items: [
      { text: "100% — every project is custom-built", slug: "custom-segmented-lcd-design" },
    ],
  },
];

// ── Scroll reveal hook ────────────────────────────────────────────────────────
function useReveal(selector) {
  useEffect(() => {
    const els = document.querySelectorAll(selector);
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("sl-in"); }),
      { threshold: 0.1 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [selector]);
}

// ══════════════════════════════════════════════════════════════════════════════
const SegmentedLCDPage = () => {
  useReveal("[data-reveal]");

  return (
    <div className="sl-root">

      {/* ══════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════ */}
      <section className="sl-hero">
        <div className="sl-wrap">
          <div className="sl-hero-grid">

            {/* Left */}
            <div className="sl-hero-left">
              <span className="sl-badge">100% CUSTOM-ENGINEERED</span>
              <h1 className="sl-hero-title">
                Segmented LCD<br />
                <span className="sl-hero-green">Displays</span>
              </h1>
              <p className="sl-hero-desc">
                <strong>Biba Electronics Pvt. Ltd.</strong> does not stock a
                catalog of standard, off-the-shelf segmented LCDs — every
                segmented display we ship is <strong>designed from a blank
                sheet</strong> around your product: the segment layout, the
                icons, the glass size, and the PCB and connector are all
                engineered to your exact requirement.
              </p>
              <div className="sl-hero-tags">
                <span className="sl-tag">Fully Custom Layout</span>
                <span className="sl-tag">Any Icon / Symbol</span>
                <span className="sl-tag">Custom PCB & Connector</span>
                <span className="sl-tag">RoHS Certified</span>
              </div>
            </div>

            {/* Right — real product photo */}
            <div className="sl-hero-right">
              <div className="sl-hero-photo-card">
                <img
                  src="/assets/images/lcd-stn-panel.jpg"
                  alt="BIBA STN-type segmented LCD glass panel"
                  className="sl-hero-photo"
                />
                <div className="sl-hero-photo-label">STN Segmented LCD Panel — Built by BIBA</div>
              </div>
            </div>

          </div>
        </div>
        <div className="sl-hero-line"></div>
      </section>

      {/* ══════════════════════════════════════════════
          SPECS STRIP
      ══════════════════════════════════════════════ */}
      <section className="sl-specs-strip" data-reveal>
        <div className="sl-wrap">
          <p className="sl-specs-hint">Click any spec below to learn more →</p>
          <div className="sl-specs-grid">
            {SPECS.map((s, i) => {
              // Every item in this row points at the same article (true for
              // Technology, where TN/HTN/STN/FSTN all link to one comparison
              // page, and for every single-value row) -- so there's no reason
              // to make the visitor aim for one word out of several. Wrap the
              // whole cell in one link instead of one per word.
              const allSameSlug = s.items.every((item) => item.slug === s.items[0].slug);

              const valueContent = s.items.map((item, j) => (
                <React.Fragment key={item.text}>
                  {j > 0 && <span className="sl-spec-sep"> / </span>}
                  {item.text}
                </React.Fragment>
              ));

              return allSameSlug ? (
                <Link to={`/learn/${s.items[0].slug}`} className="sl-spec-item sl-spec-item-link" key={i}>
                  <div className="sl-spec-label">{s.label}</div>
                  <div className="sl-spec-value">{valueContent}</div>
                </Link>
              ) : (
                // Different values here go to different articles, so the
                // whole cell can't be one link -- each value renders as its
                // own visibly-clickable chip (permanent border/background,
                // not just on hover) instead of bare separator-joined text,
                // so it's obvious which words are links and which aren't.
                <div className="sl-spec-item" key={i}>
                  <div className="sl-spec-label">{s.label}</div>
                  <div className="sl-spec-value sl-spec-chips">
                    {s.items.map((item) => (
                      <Link to={`/learn/${item.slug}`} className="sl-spec-link" key={item.text}>
                        {item.text}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          APPLICATIONS — PHOTO GRID
      ══════════════════════════════════════════════ */}
      <section className="sl-section sl-apps-section">
        <div className="sl-wrap">
          <div className="sl-section-head" data-reveal>
            <span className="sl-badge">WHERE IT'S USED</span>
            <h2 className="sl-section-title">
              Built For Your<br />
              <span className="sl-title-green">Application</span>
            </h2>
            <p className="sl-section-desc">
              A few of the product categories our custom segmented LCDs ship
              into — each one with a different segment layout, digit count and
              icon set.
            </p>
          </div>

          <div className="sl-app-grid">
            {APPLICATIONS.map((app, i) => (
              <div
                className="sl-app-card"
                key={app.key}
                data-reveal
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="sl-app-img-wrap">
                  <img src={app.image} alt={app.title} className="sl-app-img" loading="lazy" decoding="async" />
                </div>
                <h3 className="sl-app-title">{app.title}</h3>
                <p className="sl-app-desc">{app.desc}</p>
              </div>
            ))}
          </div>

          <div className="sl-app-tags" data-reveal>
            <span className="sl-app-tags-label">Also used in:</span>
            {APPLICATIONS_EXTRA.map((item) => (
              <span className="sl-tag" key={item}>{item}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          TYPES OF SEGMENTED LCDs — INFO TABLE
      ══════════════════════════════════════════════ */}
      <section className="sl-section sl-types-section">
        <div className="sl-wrap">
          <div className="sl-section-head" data-reveal>
            <span className="sl-badge">DISPLAY TECHNOLOGY</span>
            <h2 className="sl-section-title">
              Types of Segmented<br />
              <span className="sl-title-green">LCDs We Build</span>
            </h2>
            <p className="sl-section-desc">
              We choose the liquid-crystal technology and viewing mode to fit
              your budget and environment — but whichever you pick, the
              segment count, icon shapes and PCB are still drawn entirely to
              your spec.
            </p>
          </div>

          <div className="sl-table-wrap" data-reveal>
            <table className="sl-types-table">
              <thead>
                <tr>
                  <th>Example</th>
                  <th>Technology</th>
                  <th>How It Works</th>
                  <th>Contrast &amp; Viewing</th>
                  <th>Best Suited For</th>
                </tr>
              </thead>
              <tbody>
                {LCD_TYPES.map((row) => (
                  <tr key={row.type}>
                    <td className="sl-types-cell-img">
                      <img src={row.image} alt={`${row.name} (${row.type}) segmented LCD sample`} className="sl-types-img" loading="lazy" decoding="async" />
                    </td>
                    <td className="sl-types-cell-type">
                      <span className="sl-types-tag">{row.type}</span>
                      {row.name}
                    </td>
                    <td>{row.how}</td>
                    <td>{row.contrast}</td>
                    <td>{row.bestFor}</td>
                  </tr>
                ))}
                <tr className="sl-types-row-highlight">
                  <td></td>
                  <td className="sl-types-cell-type">
                    <span className="sl-types-tag sl-types-tag--alt">+ Icons</span>
                    Any of the above
                  </td>
                  <td colSpan={3}>
                    Combined with fully custom icons and symbols — battery,
                    degree, alarm, currency and any brand-specific glyph. This
                    is standard practice on every BIBA project, not an add-on option.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <span className="sl-table-scroll-hint">Swipe to see more →</span>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          WHY CHOOSE
      ══════════════════════════════════════════════ */}
      <section className="sl-section sl-why-section">
        <div className="sl-wrap">
          <div className="sl-section-head" data-reveal>
            <span className="sl-badge">WHY CHOOSE BIBA</span>
            <h2 className="sl-section-title">
              Why Choose Biba<br />
              <span className="sl-title-green">Segmented LCDs?</span>
            </h2>
            <p className="sl-section-desc">
              Designed with customizable segments, icons, symbols, and advanced backlight
              options — ensuring superior readability across all environments.
            </p>
          </div>

          <div className="sl-why-grid">
            {WHY_CARDS.map((card, i) => (
              <div
                className="sl-why-card"
                key={i}
                data-reveal
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="sl-why-icon">{card.icon}</div>
                <h3 className="sl-why-title">{card.title}</h3>
                <p className="sl-why-desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          KEY FEATURES + DESIGN PROCESS (2-col)
      ══════════════════════════════════════════════ */}
      <section className="sl-section sl-two-col-section">
        <div className="sl-wrap">
          <div className="sl-two-col-grid">

            {/* Key Features */}
            <div className="sl-info-card" data-reveal>
              <div className="sl-info-card-head">
                <div className="sl-info-icon-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polyline points="9 11 12 14 22 4"/>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                  </svg>
                </div>
                <div>
                  <span className="sl-info-chip">TECHNICAL SPECS</span>
                  <h3 className="sl-info-title">Key Features</h3>
                </div>
              </div>
              <ul className="sl-info-list">
                {KEY_FEATURES.map((item, i) => (
                  <li key={i}>
                    <span className="sl-list-dot"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Custom Design Process */}
            <div className="sl-info-card sl-info-card--alt" data-reveal>
              <div className="sl-info-card-head">
                <div className="sl-info-icon-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                </div>
                <div>
                  <span className="sl-info-chip">START TO FINISH</span>
                  <h3 className="sl-info-title">Our Custom Design Process</h3>
                </div>
              </div>
              <ul className="sl-info-list sl-info-list--numbered">
                {DESIGN_PROCESS.map((item, i) => (
                  <li key={i}>
                    <span className="sl-list-num">{i + 1}</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          CTA BANNER — same as contact page green card
      ══════════════════════════════════════════════ */}
      <section className="sl-section sl-cta-section">
        <div className="sl-wrap">
          <div className="sl-cta-banner" data-reveal>
            <div className="sl-cta-left">
              <h2 className="sl-cta-title">Need a Custom Segmented Display?</h2>
              <p className="sl-cta-desc">
                We don't keep segmented LCDs on a shelf — every project starts
                from your application and ends in a display engineered
                exactly for it, right down to the PCB and connector.
              </p>
            </div>
            <div className="sl-cta-actions">
              <a href="/contact#rfq" className="sl-btn-solid">
                Get a Quote
              </a>
              <a href="/contact" className="sl-btn-outline">
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default SegmentedLCDPage;
